import React, { useState, useCallback } from 'react';
import { TextInput, FormControl } from '@primer/react';
import { XCircleFillIcon } from '@primer/octicons-react'

interface PriceInputProps {
    value: string;
    onChange: (value: string) => void;
    type?: 'from' | 'to';
    placeholder?: string;
    currency?: string;
    className?: string;
    disabled?: boolean;
    error?: boolean;
}

const PriceInput: React.FC<PriceInputProps> = ({
    value,
    onChange,
    type = 'from',
    placeholder,
    currency = '₽',
    className = '',
    disabled = false,
    error = false
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const formatNumber = (num: string): string => {
        const cleanNum = num.replace(/\D/g, '');
        if (!cleanNum) return '';

        return cleanNum.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const getDisplayValue = (): string => {
        if (!value) return '';

        const formatted = formatNumber(value);
        if (!formatted) return '';

        const prefix = type === 'from' ? 'От ' : 'До ';
        return `${prefix}${formatted} ${currency}`;
    };

    const getRawValue = (): string => {
        return value.replace(/\D/g, '');
    };

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/\D/g, '');
        const normalized = numericValue === '' ? '' : String(parseInt(numericValue, 10));

        onChange(normalized);
    }, [onChange]);

    const handleClear = useCallback(() => {
        onChange('');
    }, [onChange]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const getPlaceholder = (): string => {
        if (placeholder) return placeholder;
        return type === 'from' ? 'От' : 'До';
    };

    return (
        <FormControl required key={type} >
            <FormControl.Label className='invisible h-0'></FormControl.Label>
            <TextInput
                trailingAction={
                    value.length > 0 ? (
                        <TextInput.Action
                            onClick={handleClear}
                            icon={XCircleFillIcon}
                            aria-label="Очистить"
                        />
                    ) : undefined
                }
                type="text"
                value={isFocused ? getRawValue() : getDisplayValue()}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={getPlaceholder()}
                disabled={disabled}
                contrast={!!(value && value.trim())}
            />
            {error && (
                <FormControl.Validation variant="error" className='invisible h-0'></FormControl.Validation>
            )}
        </FormControl>
    );
};

export default PriceInput